import styled, { keyframes, css } from 'styled-components'

const slideUp = keyframes`
  from {
    transform: translateY(100%);
  }
  to {
    transform: translateY(0);
  }
`

const spin = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.97);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`

export const Overlay = styled.section`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.48);
  z-index: 500;
  display: flex;
  align-items: flex-end;
  overscroll-behavior: none;

  @media (min-width: 769px) {
    align-items: center;
    justify-content: center;
    padding: 16px;
  }
`

export const Container = styled.div`
  font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
  background: #ffffff;
  width: 100%;
  height: 100%;
  overflow-y: auto;
  overscroll-behavior: contain;
  animation: 0.3s ${slideUp} ease-out;

  @media (min-width: 769px) {
    width: 568px;
    max-width: 100%;
    height: auto;
    max-height: 90vh;
    border-radius: 8px;
    animation: 0.25s ${fadeIn} ease-out;
  }
`

export const MobileHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 16px;
  border-bottom: 1px solid #e0e0e0;

  @media (min-width: 769px) {
    display: none;
  }
`

export const Body = styled.div`
  padding: 32px 16px;

  @media (min-width: 769px) {
    padding: 40px 24px;
  }
`

export const TitleRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 8px;
  position: relative;

  @media (min-width: 769px) {
    margin-bottom: 16px;
  }
`

export const Title = styled.h2`
  font-size: 20px;
  font-weight: 400;
  color: #212121;
  margin: 0;
  line-height: 1.3;
  margin-bottom: 8px;

  @media (min-width: 769px) {
    margin-bottom: 0;
  }
`

export const Subtitle = styled.p`
  font-size: 14px;
  font-weight: 400;
  color: #757575;
  margin: 0 0 22px 0;
  line-height: 1.5;

  strong {
    font-family: 'HelveticaNeue-Medium--AIDialog', 'Helvetica Neue', Helvetica,
      Arial, sans-serif;
    font-weight: normal;
    color: #757575;
  }
`

export const InfoAI = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 6px;
  position: relative;
  padding: 8px;
  border-radius: 4px;
  background-color: #fafafa;
`

export const InfoMessage = styled.p`
  font-size: 12px;
  font-weight: 400;
  color: #757575;
  line-height: 16px;

  strong {
    font-family: 'HelveticaNeue-Medium--AIDialog', 'Helvetica Neue', Helvetica,
      Arial, sans-serif;
    font-weight: normal;
    color: #757575;
  }
`

export const Divider = styled.hr`
  border: none;
  border-top: 1px solid #e0e0e0;
  margin: 0 0 24px 0;
`

export const DividerBottom = styled.hr`
  border: none;
  border-top: 1px solid #e0e0e0;
  margin: 32px 0;
`

export const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: transparent;
  border: none;
  cursor: pointer;
  color: #757575;
  padding: 4px;

  &:focus {
    outline: none;
  }

  &:hover {
    opacity: 0.7;
  }
`

export const DesktopCloseButton = styled(CloseButton)`
  display: none;

  @media (min-width: 769px) {
    display: flex;
    position: absolute;
    top: -20.2px;
    right: -4px;
  }
`

export const FieldsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`

export const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

export const FieldLabel = styled.label`
  font-family: 'HelveticaNeue-Medium--AIDialog', 'Helvetica Neue', Helvetica,
    Arial, sans-serif;
  font-weight: normal;
  font-size: 14px;
  color: #757575;
  line-height: 1.4;
`

export const SelectWrapper = styled.div`
  position: relative;
  width: 100%;
`

export const SelectTrigger = styled.button`
  width: 100%;
  padding: 15px;
  font-size: 14px;
  font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
  color: #9e9e9e;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  background: #ffffff;
  cursor: pointer;
  text-align: left;
  display: flex;
  align-items: center;
  justify-content: space-between;

  svg {
    flex-shrink: 0;
    transition: transform 0.2s ease;
    transform: ${({ isopen }) =>
      isopen === 'true' ? 'rotate(180deg)' : 'rotate(0deg)'};
  }

  &:focus {
    outline: none;
    border: 1px solid #90caf9;
    box-shadow: 0 0 0 1px #90caf9;
  }
`

export const SelectDropdown = styled.ul`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  background: #ffffff;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.12);
  z-index: 10;
  overflow: hidden;
  list-style: none;
  padding: 6px 0;
  margin: 0;

  transform-origin: top center;
  transition: opacity 0.15s ease, transform 0.15s ease, visibility 0.15s;

  ${({ isopen }) =>
    isopen === 'true'
      ? css`
          opacity: 1;
          transform: scaleY(1);
          visibility: visible;
          pointer-events: auto;
        `
      : css`
          opacity: 0;
          transform: scaleY(0.95);
          visibility: hidden;
          pointer-events: none;
        `}
`

export const SelectOption = styled.li`
  padding: 12px 16px;
  font-size: 14px;
  font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
  color: ${({ isselected }) => (isselected === 'true' ? '#212121' : '#757575')};
  background: ${({ isselected }) =>
    isselected === 'true' ? '#f5f5f5' : 'transparent'};
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
  font-weight: 400 !important;

  &:hover {
    background: #f5f5f5;
    color: #212121;
  }
`

export const TextAreaWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

export const TextArea = styled.textarea`
  width: 100%;
  padding: 18px 16px;
  font-size: 14px;
  font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
  color: #212121;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  background: #ffffff;
  resize: none;
  min-height: 96px;
  line-height: 1.5;

  &::placeholder {
    color: #9e9e9e;
    font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
  }

  &:focus {
    outline: none;
    border: 1px solid #90caf9;
    box-shadow: 0 0 0 1px #90caf9;
  }
`

export const CharCount = styled.span`
  font-size: 10px;
  color: #9e9e9e;
  text-align: right;
  padding-right: 16px;
`

export const RecaptchaArea = styled.div`
  margin-top: 24px;
`

export const RecaptchaBadge = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 14px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
  font-size: 11px;
  line-height: 1.6;
  color: #9e9e9e;

  a {
    color: #9e9e9e;
    text-decoration: underline;

    &:hover {
      opacity: 0.75;
    }
  }
`

export const GenerateButton = styled.button`
  width: 100%;
  margin-top: 48px;
  padding: 14px 24px;
  border-radius: var(--radius-r-05, 4px);
  border: none;
  cursor: pointer;
  font-size: 14px;
  font-weight: normal;
  font-family: 'HelveticaNeue-Medium--AIDialog', 'Helvetica Neue', Helvetica,
    Arial, sans-serif;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: background 0.2s ease, opacity 0.2s ease;

  ${({ isloading }) =>
    isloading === 'true'
      ? css`
          background: #e0e0e0;
          color: #757575;
          opacity: 0.56;
          cursor: not-allowed;
        `
      : css`
          background: linear-gradient(225deg, #737cf6 0%, #73bcf6 100%);
          color: #ffffff;
          opacity: 1;

          &:hover:not(:disabled) {
            opacity: 0.9;
          }

          &:disabled {
            opacity: 0.5;
            cursor: not-allowed;
          }
        `}

  &:focus {
    outline: none;
  }

  @media (min-width: 769px) {
    padding: 18px 32px;
    font-size: 16px;
    margin-top: 40px;
  }
`

export const ButtonIcon = styled.span`
  display: flex;
  align-items: center;

  svg {
    width: 20px;
    height: 20px;
  }

  @media (min-width: 769px) {
    svg {
      width: 24px;
      height: 24px;
    }
  }
`

export const SpinnerWrapper = styled.span`
  display: flex;
  align-items: center;
  animation: ${spin} 1s linear infinite;
`

export const InfoIcon = styled.span`
  display: flex;
  align-items: center;

  svg {
    width: 16px;
    height: 16px;
  }
`
